import React, { useState, useEffect } from 'react';
import { connect } from '../../../redux/store';
import {
  actions,
  selectIsPortfolioOwner,
} from '../../../redux/modules/settingsPortfolio';
import { I18n } from '@kineticdata/react';
import { Link } from '@reach/router';
import {
  Card,
  CardCol,
  CardRow,
  Countdown,
  ErrorMessage,
  LoadingMessage,
  TimeAgo,
  C,
  addToast,
  addToastAlert,
  openConfirm,
} from '@kineticdata/bundle-common';
import { getCatalogRevisionLockData } from '../../../utils';
import { PageTitle } from '../../shared/PageTitle';
import { ContentManager } from '../../shared/content_manager/ContentManager';
import { LockController } from './LockController';
import {
  CONTENT_MANAGER_MODE,
  getStatusColor,
} from '../../shared/content_manager/helpers';
import { List } from 'immutable';

const PortfolioRevisionComponent = ({
  portfolioId,
  revisionId,
  mode,
  // Selected revision
  revision,
  revisionError,
  revisionLoading,
  // Are user actions pending
  actionsPending,
  // Current portfolio
  portfolio,
  portfolioError,
  portfolioLoading,
  // Actions
  fetchPortfolioRevision,
  savePortfolioRevision,
  deletePortfolioRevision,
  submitPortfolioRevision,
  lockPortfolioRevision,
  resetPortfolioRevision,
  // Other props
  navigate,
  profile,
  isOwner,
  isRevisionOwner,
  appLocation,
  isMobile,
}) => {
  // Fetch the revision record when revisionId or portfolioId changes
  useEffect(
    () => {
      if (revisionId) {
        fetchPortfolioRevision(revisionId, portfolioId);
      }
      return () => {
        resetPortfolioRevision(revisionId);
      };
    },
    [fetchPortfolioRevision, resetPortfolioRevision, revisionId, portfolioId],
  );

  // State for tracking if the content manager is dirty
  const [isDirty, setIsDirty] = useState(false);

  const isRevisionDraft =
    !!revision && revision.values[C.FIELD_STATUS] === 'Draft';
  const isRevisionCompleted =
    !isRevisionDraft &&
    !!revision &&
    revision.values[C.FIELD_STATUS] !== 'Pending';
  const isPortfolioDraft =
    !!portfolio &&
    portfolio.values &&
    portfolio.values[C.FIELD_STATUS] === 'Draft';
  const doesRevisionHaveChildren =
    !!revision && revision.children && revision.children.length > 0;
  const pathToRevision = `..${!!mode ? '/..' : ''}`;
  const pathToPortfolio = `../${pathToRevision}`;
  const pathToPortfolios = `../${pathToPortfolio}`;

  const contentMode =
    !revision || !isRevisionDraft || !isRevisionOwner
      ? CONTENT_MANAGER_MODE.REVIEW
      : !!mode
        ? CONTENT_MANAGER_MODE.SUBMIT
        : CONTENT_MANAGER_MODE.EDIT;

  // Lock info
  const lockingEnabled =
    contentMode === CONTENT_MANAGER_MODE.EDIT ||
    contentMode === CONTENT_MANAGER_MODE.SUBMIT;
  const { isLocked, isLockedByMe, lockTimeLeft } = getCatalogRevisionLockData(
    revision,
    profile.username,
  );
  // State and effect for keeping track of whether this submission has been
  // locked by me at any point.
  const [hasBeenLockedByMe, setHasBeenLockedByMe] = useState(!!isLockedByMe);
  useEffect(
    () => {
      if (!!isLockedByMe) setHasBeenLockedByMe(true);
    },
    [isLockedByMe],
  );

  const handleSavePortfolioRevision = opts => {
    savePortfolioRevision({
      ...opts,
      success: () => addToast('Changes saved successfully'),
      failure: error => {
        addToastAlert({
          severity: 'danger',
          title: 'Failed to save changes',
          message:
            error.statusCode === 403 && isLocked && !isLockedByMe
              ? 'This revision is currently locked by another user'
              : error.message,
        });
      },
    });
  };

  const handleDeletePortfolioRevision = () => {
    if (!isRevisionDraft) {
      addToastAlert({
        severity: 'warning',
        title: 'Cannot delete pending revisions',
        message:
          'Please wait for pending revision to be resolved before deleting the revision.',
      });
    } else {
      openConfirm({
        title: 'Delete Portfolio Revision',
        body: (
          <div>
            <span className="d-block mb-3">
              Are you sure you want to delete this revision?{' '}
              {isPortfolioDraft &&
                'This portfolio has not yet been published. Deleting this revisision is the same as deleting the portfolio.'}
            </span>
            <em>
              This action is immediate, does not go through an approval process,
              and cannot be undone.
            </em>
          </div>
        ),
        actionName: 'Delete',
        ok: () =>
          deletePortfolioRevision({
            submissionId: revisionId,
            cancel: doesRevisionHaveChildren,
            refetchItem: !isPortfolioDraft,
            success: submission => {
              navigate(isPortfolioDraft ? pathToPortfolios : pathToPortfolio);
              addToast('Revision successfully deleted');
            },
            failure: error => {
              addToastAlert({
                severity: 'danger',
                title: 'Failed to delete revision',
                message:
                  error.statusCode === 403 && isLocked && !isLockedByMe
                    ? 'This revision is currently locked by another user'
                    : error.message,
              });
            },
          }),
      });
    }
  };

  return (
    <div className="page-container">
      {lockingEnabled && (
        <LockController
          revisionId={revisionId}
          entityId={portfolioId}
          revision={revision}
          isLocked={isLocked}
          isLockedByMe={isLockedByMe}
          hasBeenLockedByMe={hasBeenLockedByMe}
          lockTimeLeft={lockTimeLeft}
          lockRevision={lockPortfolioRevision}
          fetchRevision={fetchPortfolioRevision}
        />
      )}
      <div className="page-panel">
        <PageTitle
          className="page-title-heading--sm"
          parts={[portfolio && portfolio.values['Name'], 'Catalog Management']}
          suptitle="PORTFOLIO"
          title={
            <I18n
              render={translate => (
                <>
                  {portfolio && translate(portfolio.values['Name'])}
                  {revision && (
                    <span className="h4">
                      {portfolio && ` \u00B7 `}
                      {translate('Revision')} {revision.handle}
                    </span>
                  )}
                </>
              )}
            />
          }
          breadcrumbs={[
            {
              label: (
                <span>
                  <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                  <I18n>{C.SETTINGS_LABEL}</I18n>
                </span>
              ),
              to: '/settings',
            },
            {
              label: 'Catalog Management',
              to: `${appLocation}/settings/catalog/portfolios`,
            },
            portfolio && {
              label: portfolio.values['Name'],
              to: pathToPortfolio,
            },
          ]}
          actions={
            !revisionError &&
            revision && [
              {
                icon: !revisionLoading ? 'sync' : 'sync fa-spin',
                aria: 'Reload Revision',
                onClick: () => fetchPortfolioRevision(revisionId, portfolioId),
              },
              // Delete revision action
              !isRevisionCompleted &&
                (profile.isSMO || (isOwner && !isPortfolioDraft)) && {
                  label: 'Delete Revision',
                  onClick: handleDeletePortfolioRevision,
                  disabled: !isLockedByMe || actionsPending,
                  className: 'text-danger',
                  menu: true,
                },
            ]
          }
        />

        {revisionError ? (
          <ErrorMessage
            title="Failed to retrieve portfolio revision."
            message={revisionError.message}
          />
        ) : !revision && revisionLoading ? (
          <LoadingMessage />
        ) : (
          !!revision && (
            <>
              <div className="cards">
                <Card bar={true} barSize="xs" barColor="primary">
                  <CardCol>
                    <CardRow type="title">
                      <span>
                        <I18n>
                          {contentMode === CONTENT_MANAGER_MODE.EDIT
                            ? 'Edit Revision'
                            : contentMode === CONTENT_MANAGER_MODE.SUBMIT
                              ? 'Review and Submit Revision'
                              : revision.values[C.FIELD_STATUS] === 'Pending'
                                ? 'Revision Pending Approval'
                                : 'Review Revision'}
                        </I18n>
                      </span>
                      <div className="ml-auto">
                        {contentMode === CONTENT_MANAGER_MODE.REVIEW && (
                          <Link
                            className="btn btn-outline-dark"
                            to={
                              isRevisionCompleted
                                ? pathToPortfolio
                                : pathToRevision
                            }
                          >
                            <span className="fa fa-arrow-left fa-fw" />
                            <I18n>Return to Portfolio</I18n>
                          </Link>
                        )}
                        {contentMode === CONTENT_MANAGER_MODE.EDIT && (
                          <>
                            <button
                              className="btn btn-outline-dark"
                              onClick={() => {
                                if (!isDirty) {
                                  navigate(pathToRevision);
                                } else {
                                  openConfirm({
                                    title: 'Unsaved Changes',
                                    body:
                                      'You have unsaved changes that will be lost if you continue. Are you sure you want to exit edit mode?',
                                    actionName: 'Exit Edit Mode',
                                    ok: () => navigate(pathToRevision),
                                  });
                                }
                              }}
                            >
                              <span className="fa fa-arrow-left fa-fw" />
                              <I18n>Exit Edit Mode</I18n>
                            </button>
                            <button
                              className="btn btn-primary ml-2"
                              onClick={() => navigate('submit')}
                              disabled={isDirty || !isLockedByMe}
                            >
                              <I18n>Review and Submit</I18n>
                            </button>
                          </>
                        )}
                        {contentMode === CONTENT_MANAGER_MODE.SUBMIT && (
                          <>
                            <Link className="btn btn-outline-dark" to="..">
                              <span className="fa fa-arrow-left fa-fw" />
                              <I18n>Continue Editing</I18n>
                            </Link>
                            <button
                              className="btn btn-primary ml-2"
                              onClick={() =>
                                submitPortfolioRevision({
                                  submissionId: revision.id,
                                  // Use updatedAt as a pseudo version id to
                                  // ensure we're submitting the latest version
                                  updatedAt: revision.updatedAt,
                                  success: submission => {
                                    navigate(pathToRevision);
                                    addToast(
                                      'Revision successfully submitted for approval',
                                    );
                                  },
                                  failure: error => {
                                    addToastAlert({
                                      severity: 'danger',
                                      title: 'Failed to submit revision',
                                      message: error.message,
                                    });
                                  },
                                })
                              }
                              disabled={!isLockedByMe}
                            >
                              <I18n>Submit for Approval</I18n>
                            </button>
                          </>
                        )}
                      </div>
                    </CardRow>
                    <CardRow className="text-muted" type="meta">
                      <dl className="flat align-items-center">
                        <div>
                          <dd>
                            <span
                              className={`badge badge-${getStatusColor(
                                revision.values['Status'],
                              )} badge-stylized text-muted`}
                            >
                              <I18n>{revision.values['Status']}</I18n>
                            </span>
                          </dd>
                        </div>
                        <div>
                          <dt>
                            <I18n>Created</I18n>
                          </dt>
                          <dd>
                            <TimeAgo timestamp={revision.createdAt} />
                          </dd>
                        </div>
                        <div>
                          <dt>
                            <I18n>Updated</I18n>
                          </dt>
                          <dd>
                            <TimeAgo timestamp={revision.updatedAt} />
                          </dd>
                        </div>
                      </dl>
                    </CardRow>
                    {lockingEnabled && (
                      <CardRow className="mt-2">
                        {isLockedByMe ? (
                          <>
                            <span className="badge badge-success badge-pill">
                              <span className="fa fa-lock mr-1" />
                              <I18n>Locked By You</I18n>
                              {' \u00B7 '}
                              <Countdown
                                date={new Date().getTime() + lockTimeLeft}
                              />
                            </span>
                            <button
                              className="btn btn-text ml-2"
                              onClick={() =>
                                lockPortfolioRevision({
                                  submissionId: revision.id,
                                })
                              }
                            >
                              Renew Lock
                            </button>
                          </>
                        ) : isLocked ? (
                          <>
                            <span className="badge badge-danger badge-pill">
                              <span className="fa fa-lock mr-1" />
                              <I18n>Locked By Another User</I18n>
                            </span>
                            <button
                              className="btn btn-text ml-2"
                              onClick={() =>
                                fetchPortfolioRevision(revisionId, portfolioId)
                              }
                            >
                              Refresh
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="badge badge-subtle badge-pill">
                              <span className="fa fa-lock-open mr-1" />
                              <I18n>
                                {hasBeenLockedByMe
                                  ? 'Lock Expired'
                                  : 'Not Locked'}
                              </I18n>
                            </span>
                            <button
                              className="btn btn-text ml-2"
                              onClick={() =>
                                lockPortfolioRevision({
                                  submissionId: revision.id,
                                })
                              }
                            >
                              Obtain Lock
                            </button>
                          </>
                        )}
                      </CardRow>
                    )}
                  </CardCol>
                </Card>
              </div>

              <ContentManager
                submission={revision}
                save={handleSavePortfolioRevision}
                actionsPending={actionsPending}
                imageDir={`Portfolio/${revision.values[C.FIELD_PORTFOLIO_ID]}`}
                username={profile.username}
                // If in EDIT mode but the user never had a lock, show in review
                // mode. If user did have a lock at any point, show in EDIT mode
                // to prevent changes from getting lost when the lock is lost.
                mode={
                  contentMode === CONTENT_MANAGER_MODE.EDIT &&
                  !hasBeenLockedByMe
                    ? CONTENT_MANAGER_MODE.REVIEW
                    : contentMode
                }
                // Provide a function to be called when the dirty flag changes
                // so that we can use the dirty state in this component.
                onDirtyChange={setIsDirty}
              />
            </>
          )
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const portfolios =
    state.settingsPortfolio.data.get(props.portfolioId) || List();
  const portfolio =
    state.settingsPortfolio.current &&
    state.settingsPortfolio.current.values[C.FIELD_PORTFOLIO_ID] ===
      props.portfolioId
      ? state.settingsPortfolio.current
      : portfolios.first();
  const revision = state.settingsPortfolio.revision;
  return {
    // State for selected revision
    revision,
    revisionLoading: state.settingsPortfolio.revisionLoading,
    revisionError: state.settingsPortfolio.revisionError,
    // State for current portfolio
    portfolio,
    portfolioContentReady: portfolio && portfolio.values['Content'],
    portfolioLoading: state.settingsPortfolio.currentLoading,
    portfolioError: state.settingsPortfolio.currentError,
    // State showing if any user actions are pending
    actionsPending: state.settingsPortfolio.actionsPending > 0,
    // Other state
    profile: state.app.profile,
    isOwner: selectIsPortfolioOwner(state.app.profile, portfolio),
    isRevisionOwner: selectIsPortfolioOwner(state.app.profile, revision),
    appLocation: state.app.location,
    layoutSize: state.app.layoutSize,
    isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  };
};

const mapDispatchToProps = {
  fetchPortfolioRevision: actions.fetchPortfolioRevisionRequest,
  savePortfolioRevision: actions.savePortfolioRevisionRequest,
  deletePortfolioRevision: actions.deletePortfolioRevisionRequest,
  submitPortfolioRevision: actions.submitPortfolioRevisionRequest,
  lockPortfolioRevision: actions.lockPortfolioRevisionRequest,
  resetPortfolioRevision: actions.resetPortfolioRevisionRequest,
};

export const PortfolioRevision = connect(
  mapStateToProps,
  mapDispatchToProps,
)(PortfolioRevisionComponent);
